import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import {
  OptionPairWithExpiration,
  SizeType,
  OrderAction,
  OptionType,
  AssetSymbol,
  BlockType,
  OptionChainSettings,
  Expiration,
  ExtendedExpiration,
  OptionExpirationType,
} from '@tradingblock/types';
import {
  Column,
  FrozenHeaderTable,
  Number,
  DropdownWrapper,
  CellProps,
  EmptyBlockContent,
  BasicSelect,
  Loading,
  ParsedMs,
  useParsedCountdown,
} from '@tradingblock/components';
import {
  TableData,
  TableDateHeaderData,
  TableOptionPairData,
  OptionChainDataType,
  isOptionChainField,
  isTableOptionPairData,
  isOptionChainVolumeField,
} from './OptionChainTypes';
import { ExpirationOptionPair } from '../../types';
import {
  DisplayTimeTilClose,
  expirationValue,
  isBetweenMidnightInclusiveAndOpeningHoursExclusive,
  isSameDate,
  isToday,
} from '../../utilities/date';
import { RenderExpirationRow } from './components/RootExpirationRow';
import { useOptionChainData } from './state/OptionChainState';
import { useOptionChain } from './data/useOptionChain';
import { BidAskMemo } from './components/BidAsk';
import { OptionPairVolumeMemo } from './components/OptionPairVolume';
import { AddRemoveFavorite } from './AddRemoveFavorite';
import {
  IndexMarketCloseTimeSelector,
  isMarketOpenSelector,
  MarketCloseTimeSelector,
  MarketOpenTimeSelector,
  useStateSelector,
} from '../../data/global/dataSelectors';
import { useOptionChainSettings } from './data/useOptionChainSettings';
import { ExpirationsFilter } from '../shared/ExpirationsFilter';
import { useBlockMetadata } from '../../components/blocks/BlockState';
import { blockSettings } from '../../data/global/selectors/settingSelectors';
import { generateNewBlock } from '../../utilities/block';
import { useDispatcher } from '../../data/global/hooks';
import { useOptionChainActions } from './state/useOptionChainActions';
import { toShort } from '@tradingblock/api';
import { OptionChain, SYMBOLS_THAT_CAN_TRADED_AFTER_MARKET_CLOSE } from '../../constants';
import { OptionChainTableRow } from './components/OptionChainTableRow';
import { dataStore } from '../../AppStoreProvider';
import dayjs, { Dayjs } from 'dayjs';
import { expirationDisplayInfo } from '../../utilities/data';

const StrikeField = 'Strike';
const dataOptionsWithoutStrikePriceCompare = {
  tdClass: (cell: any) => {
    if (cell.column.id === StrikeField) {
      return 'strike default-cursor';
    }
    const isCallOption = _.includes(cell.column.id, 'Call.');
    const showPosNeg = _.includes(cell.column.id, '.Change') || _.includes(cell.column.id, '.Delta');
    const changeClass = showPosNeg ? (cell.value > 0 ? 'pos' : cell.value < 0 ? 'neg' : '') : '';
    return `option-type-${isCallOption ? 'call' : 'put'} ${changeClass}`;
  },
};

function getCellOptionData<T extends TableOptionPairData>(original: T) {
  return { expiration: original.expiration, strike: original.Strike };
}
function getCellOccSymbol<T extends TableOptionPairData>(original: T, type: OptionType) {
  return type === OptionType.Call ? original.Call.Symbol : original.Put.Symbol;
}

const getColumnHeader = (column: string) => {
  if (column === 'ImpliedVolatility') {
    return 'IV';
  }
  if (column === 'OpenInterest') {
    return 'Open int.';
  }
  return column;
};
// columns for each option call/put in chain pair
const optionCallColumns = [
  'Volume',
  'ImpliedVolatility',
  'Close',
  'Change',
  'Last',
  'Vega',
  'Gamma',
  'Theta',
  'Delta',
  'OpenInterest',
  'Bid',
  'Ask',
];

const optionPutColumns = [
  'Bid',
  'Ask',
  'OpenInterest',
  'Delta',
  'Theta',
  'Gamma',
  'Vega',
  'Last',
  'Change',
  'Close',
  'ImpliedVolatility',
  'Volume',
];

const columns: any[] = [
  // call columns
  ..._.map(optionCallColumns, c => {
    return {
      Header: getColumnHeader(c),
      accessor: `Call.${c}`,
    };
  }),
  {
    Header: <div className="strike">{StrikeField}</div>,
    accessor: StrikeField,
  },
  // put columns
  ..._.map(optionPutColumns, c => ({
    Header: getColumnHeader(c),
    accessor: `Put.${c}`,
  })),
];
type ColumnWithWidth<D extends object = {}> = Column<D> & {
  width?: number;
};
export const OptionChainTable: FunctionComponent<{
  orderActionForBid: OrderAction;
  width: SizeType;
  symbol: AssetSymbol;
}> = ({ orderActionForBid, width, symbol }) => {
  const { blockId, groupId } = useBlockMetadata();
  const settings = useStateSelector(s => blockSettings.optionChain(s, blockId));
  const [rowValues1, setRowValues1] = useState<TableOptionPairData[]>([]);
  const [rowValues2, setRowValues2] = useState<TableOptionPairData[]>([]);
  const { setExpandedExpirations } = useOptionChainActions();
  const [price, setPrice] = useState<number | null>(null);
  const [strikeCount, setStrikeCount] = useState<number | null>(null);
  const isMarketOpen = useStateSelector(isMarketOpenSelector);
  const isBeforeMarketOpen = isBetweenMidnightInclusiveAndOpeningHoursExclusive();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const runCheck = () => {
      const state = dataStore.getState();
      const quote = state.quotes.quoteMetadata.data[symbol ? symbol.symbol : ''];

      if (!quote) {
        timeoutId = setTimeout(runCheck, 10);
        return;
      }

      const { BidPrice, LastTradePrice } = quote;

      const price =
        BidPrice && BidPrice > 0 ? BidPrice : LastTradePrice && LastTradePrice > 0 ? LastTradePrice : undefined;

      if (!price) {
        timeoutId = setTimeout(runCheck, 10);
        return;
      } else {
        setPrice(price);
      }

      timeoutId = setTimeout(runCheck, 10000); // schedule next run
    };

    runCheck(); // run immediately

    return () => clearTimeout(timeoutId); // clean up on unmount
  }, [symbol, rowValues1, rowValues2]);

  const { weeklyExpirations, monthlyExpirations, quarterlyExpirations, yearlyExpirations } = settings;

  const expandedExpirations = useOptionChainData(s => s.expandedExpirations);
  const { optionchain2 } = useOptionChain(symbol.symbol);
  const { current } = useOptionChainSettings();
  const { dispatcher } = useDispatcher();

  const isTodayAndMarketOpen = (date: Date | dayjs.Dayjs, isMarketOpen: boolean | undefined) =>
    (isSameDate(date, dayjs()) && (isMarketOpen || isBeforeMarketOpen)) || dayjs().isBefore(date);

  const filteredOptionChain = useMemo(() => {
    // filter out expirations that are not selected based on expirationFreqCode
    const filtered = _.filter(optionchain2, o => {
      const expirationFreqCode = o.expiration.ExpirationFreqCode;
      const isWeekly =
        expirationFreqCode === 'W' ||
        expirationFreqCode === 'MW' ||
        expirationFreqCode === 'WW' ||
        (expirationFreqCode && expirationFreqCode.includes('Week')) ||
        (expirationFreqCode && expirationFreqCode.includes('W'));

      const isMonthly =
        expirationFreqCode === 'M' ||
        expirationFreqCode === 'MM' ||
        expirationFreqCode === 'Monthly' ||
        expirationFreqCode === 'VX' ||
        expirationFreqCode === 'ME';
      const isQuarterly = expirationFreqCode === 'QuarterlyEOM';
      const isYearly = expirationFreqCode === 'Yearly';

      // If market is closed and this is today's expiration, filter it out
      if (!(isBeforeMarketOpen || isMarketOpen) && isSameDate(o.expiration.date, dayjs())) {
        return false;
      }

      return (
        (isWeekly && weeklyExpirations) ||
        (isMonthly && monthlyExpirations) ||
        (isQuarterly && quarterlyExpirations) ||
        (isYearly && yearlyExpirations)
      );
    });
    return filtered;
  }, [
    optionchain2,
    weeklyExpirations,
    monthlyExpirations,
    quarterlyExpirations,
    yearlyExpirations,
    isMarketOpen,
    isBeforeMarketOpen,
  ]);

  const onFavoriteAdd = () => {
    const favoritesBlock = generateNewBlock(BlockType.Favorites);
    dispatcher.block.addOrUpdateBlock({ ...favoritesBlock, groupId }, true, { blockType: BlockType.Favorites });
  };
  const elementRef = useRef(null);

  const columnsDisplayed: ColumnWithWidth<any>[] = useMemo(() => {
    // hide some columns based on width...
    const hiddenColumns: string[] = [
      // hide 'greeks' columns if not large view
      ...(width !== SizeType.lg ? ['Delta', 'ImpliedVolatility', 'Theta', 'Gamma', 'Vega', 'Volume'] : []),
      // hide Last/Change columns if not med/large view
      ...(width !== SizeType.lg && width !== SizeType.md ? ['Last', 'Change', 'Close'] : []),
    ];

    // filter out hidden columns...
    let cols = _.filter(columns, c => !_.includes(hiddenColumns, c.accessor.substr(c.accessor.lastIndexOf('.') + 1)));

    const headerToSettingKeyMap: { [key: string]: keyof OptionChainSettings } = {
      'Open int.': 'OpenInterest',
      // Add mappings for other headers if necessary.
    };

    cols = cols.filter(column => {
      const header = getColumnHeader(column.Header);
      if (column.accessor === 'Strike') {
        return column;
      }

      if (column.accessor.includes('OpenInterest')) {
        const settingKey = headerToSettingKeyMap[header];
        return current[settingKey as keyof typeof useOptionChainSettings];
      }

      return current[header as keyof typeof useOptionChainSettings];
    });
    // include Cell prop to display values as order dropdowns
    return _.map(cols, c => ({
      ...c,
      width: 1,
      Cell: ({ cell: { value, row } }: CellProps<OptionChainDataType>) => {
        const fieldName = c.accessor;
        const action = fieldName.substr(fieldName.indexOf('.') + 1) as string;
        const optionType: OptionType =
          fieldName.replace('.' + action, '') === 'Call' ? OptionType.Call : OptionType.Put;
        // for change cell, prefix positive values with plus sign (negative values will be natively rendered with minus)
        const prefix = _.includes(fieldName, '.Change') && value > 0 ? '+' : '';
        const integer = _.includes(fieldName, '.Volume') || _.includes(fieldName, '.OpenInterest');
        const percent = _.includes(fieldName, '.ImpliedVolatility');
        const isStrikeField = fieldName === StrikeField;
        const greeks =
          _.includes(fieldName, '.Delta') ||
          _.includes(fieldName, '.Theta') ||
          _.includes(fieldName, '.Gamma') ||
          _.includes(fieldName, '.Vega') ||
          _.includes(fieldName, '.ImpliedVolatility');
        const cell = fieldName.includes('.Gamma') ? (
          <>{value && value.toFixed(3)}</>
        ) : (
          <Number
            value={value}
            prefix={prefix}
            format
            integer={integer}
            greeks={greeks}
            percent={percent}
            isStrikeField={isStrikeField}
          />
        );
        // only show dropdowns for Bid/Ask columns
        const pairData = row.original;
        const isMiddleColumn = fieldName === StrikeField;
        if (isOptionChainField(action) && symbol && isTableOptionPairData(pairData)) {
          const optionData = getCellOptionData(pairData);
          const cellSym = getCellOccSymbol(pairData, optionType);
          return (
            <BidAskMemo
              symbol={cellSym}
              optionType={optionType}
              field={action}
              orderActionForBid={orderActionForBid}
              {...optionData}
              expiration={optionData.expiration}
            />
          );
        } else if (isOptionChainVolumeField(action) && symbol && isTableOptionPairData(pairData)) {
          const optionData = getCellOptionData(pairData);
          return <OptionPairVolumeMemo optionType={optionType} {...optionData} />;
        } else if (isTableOptionPairData(pairData) && !isMiddleColumn) {
          if (symbol !== undefined) {
            const cellAssetSymbol = { ...symbol, symbol: getCellOccSymbol(pairData, optionType) };
            return (
              <DropdownWrapper
                direction="down"
                content={<AddRemoveFavorite onFavoriteAdd={onFavoriteAdd} symbol={cellAssetSymbol} />}
              >
                {cell}
              </DropdownWrapper>
            );
          }
        } else {
          return cell;
        }
        return cell;
      },
    }));
  }, [width, symbol, orderActionForBid, current, expandedExpirations && expandedExpirations[0]]);
  const pairs: OptionPairWithExpiration[] = useMemo(() => {
    // Create a new array without mutating the original to avoid stale closures
    const expirationsToUse =
      expandedExpirations && expandedExpirations.length > 2 ? expandedExpirations.slice(1) : expandedExpirations;
    const simpleExpires = _.map(expirationsToUse, ee => expirationValue(ee));
    return _(filteredOptionChain)
      .flatMap(o =>
        simpleExpires.includes(expirationValue(o.expiration))
          ? _.map(o.pairs, p => ({ ...p, expiration: o.expiration }))
          : []
      )
      .value();
  }, [filteredOptionChain, expandedExpirations]);

  useEffect(() => {
    if (expandedExpirations && strikeCount !== current.strikeCount) {
      expandedExpirations.forEach(e => {
        e.index = undefined;
        e.topStrike = undefined;
        e.bottomStrike = undefined;
      });
      setStrikeCount(current.strikeCount);
    }
  }, [current, strikeCount, setStrikeCount]);

  const dataDisplayed = useMemo(() => {
    // build array of rows for table
    const defaultTableOptionPairData: TableData = [];

    const results = _.reduce(
      filteredOptionChain,
      (rows: TableData, d: ExpirationOptionPair) => {
        if (isTodayAndMarketOpen(d.expiration.date, isMarketOpen)) {
          // include header row for each expiration
          // eslint-disable-next-line react/display-name
          const initialPairValue: TableDateHeaderData = {
            ...d,
            display: {
              renderRow: (row: any) => <RenderExpirationRow row={row} numColumns={columnsDisplayed.length} />,
            },
          };
          let rowValues: TableData = [initialPairValue];
          // include rows of chain pairs if expiration is expanded
          const matchingExpanded = _.find(
            expandedExpirations,
            e => expirationValue(e) === expirationValue(d.expiration)
          );
          if (matchingExpanded) {
            const matchingPairs = _.filter(
              pairs,
              p =>
                expirationValue(p.expiration) === expirationValue(d.expiration) &&
                p.symbol === matchingExpanded.rootSymbol
            );
            const expandedRows: TableOptionPairData[] = _.map(matchingPairs, x => ({
              ...x,
              expiration: d.expiration,
              display: dataOptionsWithoutStrikePriceCompare,
            }));
            rowValues = [...rowValues, ...expandedRows];

            if (price) {
              const closestStrike = _.minBy(expandedRows, r => Math.abs(r.Strike - price));
              // set the isClosestToPrice property to true for the strike closest to the price
              if (closestStrike) {
                const closestStrikeIndex = _.findIndex(expandedRows, r => r.Strike === closestStrike.Strike);
                if (closestStrikeIndex > -1) {
                  expandedRows[closestStrikeIndex].isClosestToPrice = true;

                  if (expandedExpirations) {
                    if (
                      expandedExpirations[0] &&
                      expirationValue(expandedExpirations[0]) === expirationValue(d.expiration)
                    ) {
                      const temp = expandedExpirations[0];
                      if (temp) {
                        temp.price = price;
                      }
                    }
                    if (
                      expandedExpirations[1] &&
                      expirationValue(expandedExpirations[1]) === expirationValue(d.expiration)
                    ) {
                      const temp2 = expandedExpirations[1];
                      if (temp2) {
                        temp2.price = price;
                      }
                    }
                  }
                }
              }
            }
            if (
              expandedRows.length > 0 &&
              expandedExpirations &&
              expandedExpirations[0] &&
              expirationValue(expandedExpirations[0]) === expirationValue(expandedRows[0].expiration)
            ) {
              let currentRowStrike = null;
              if (rowValues1.length > 0) currentRowStrike = rowValues1[0].Strike;
              if (currentRowStrike != expandedExpirations[0].currentStrike) {
                const startingStrikeIndex = expandedRows.findIndex(
                  e => e.Strike === expandedExpirations[0].currentStrike
                );
                const index = startingStrikeIndex !== -1 ? startingStrikeIndex : 0;
                const newArr = [...expandedRows].slice(index, index + 10);
                setRowValues1(newArr);
              }
            }
            if (
              expandedRows.length > 0 &&
              expandedExpirations &&
              expandedExpirations[1] &&
              expirationValue(expandedExpirations[1]) === expirationValue(expandedRows[0].expiration)
            ) {
              let currentRowStrike2 = null;
              if (rowValues2.length > 0) currentRowStrike2 = rowValues2[0].Strike;
              if (currentRowStrike2 != expandedExpirations[1].currentStrike) {
                const startingStrikeIndex2 = expandedRows.findIndex(
                  e => e.Strike === expandedExpirations[1].currentStrike
                );
                const index2 = startingStrikeIndex2 !== -1 ? startingStrikeIndex2 : 0;
                const newArr2 = [...expandedRows].slice(index2, index2 + 10);
                setRowValues2(newArr2);
              }
            }
          }
          return [...rows, ...rowValues];
        }

        return rows;
      },
      defaultTableOptionPairData
    );
    return _.orderBy(results, v => expirationValue(v.expiration));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredOptionChain,
    expandedExpirations,
    dataOptionsWithoutStrikePriceCompare,
    pairs,
    price,
    isMarketOpen,
    isBeforeMarketOpen,
  ]);
  const show = _.size(dataDisplayed) > 0;
  const columnWidths = useMemo(() => {
    const totalWidth = _.sumBy(columnsDisplayed, c => c.width || 1);
    return _.map(columnsDisplayed, c => {
      const proportion = (c.width || 1) / totalWidth;
      return `${proportion * 100}%`;
    });
  }, [columnsDisplayed]);

  const expirationsDates = useMemo(() => {
    const data = filteredOptionChain
      .map(row => {
        // if (isTodayAndMarketOpen(row.expiration.date, isMarketOpen)) {
        //   return { label: toShort(row.expiration.date), value: row.expiration };
        // }
        // return null;
        const d = dayjs(row.expiration.date);
        const expDisplayInfo = expirationDisplayInfo(row.expiration, symbol);
        const stringValue = `${d.format('MM.D.YY')} ${expDisplayInfo.label}`;
        return { label: stringValue, value: row.expiration };
      })
      .filter(item => item !== null);
    return _.orderBy(data, v => expirationValue(v.value));
  }, [filteredOptionChain, symbol]);

  const now = dayjs.utc();
  const marketOpen = useStateSelector(s => isMarketOpenSelector(s));
  const marketOpenTime = useStateSelector(s => MarketOpenTimeSelector(s));
  const marketCloseTime = useStateSelector(s => MarketCloseTimeSelector(s));
  const dateForDiff =
    (!marketOpen && isBetweenMidnightInclusiveAndOpeningHoursExclusive() && now.isBefore(marketOpenTime)) ||
    (marketOpen && now.isBefore(marketCloseTime))
      ? dayjs.utc().add(-1, 'day')
      : dayjs.utc();

  return (
    <div ref={elementRef}>
      <ExpirationsFilter />
      {!show && (
        <EmptyBlockContent title="No Options Available">
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-2x fa-search-dollar" /> No options are available given the current filters and symbol.
            Try adjusting your filters to see more.
          </span>
        </EmptyBlockContent>
      )}
      {show && expandedExpirations && expandedExpirations.length === 0 && (
        <FrozenHeaderTable
          columnWidths={columnWidths}
          className="chain"
          tableKey="optionsChain"
          responsiveDisabled={true}
          columns={columnsDisplayed}
          data={dataDisplayed}
        />
      )}
      {expandedExpirations && expandedExpirations[0] && (
        <>
          <SplitOptionChainView
            index={0}
            expandedExpirations={expandedExpirations}
            setExpandedExpirations={setExpandedExpirations}
            symbol={symbol}
            dateForDiff={dateForDiff}
            rowValues2={rowValues2}
            setRowValues1={setRowValues1}
            setRowValues2={setRowValues2}
          />
          {rowValues1.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ textAlign: 'center', width: '100%', fontSize: '0.75rem' }}>
                <Loading size="small" />
                <br />
              </div>
            </div>
          ) : (
            <FrozenHeaderTable
              columnWidths={columnWidths}
              RowComponent={OptionChainTableRow}
              className="chain"
              tableKey="optionsChain"
              responsiveDisabled={true}
              columns={columnsDisplayed}
              data={rowValues1}
            />
          )}
          {expandedExpirations && expandedExpirations.length < 2 && (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ textAlign: 'center', width: '100%', fontSize: '0.75rem' }}>
                <>
                  <br />
                  <BasicSelect
                    className="btn btn-dark btn-tall dropdown-toggle"
                    options={expirationsDates}
                    onChange={(option: { label: string; value: Expiration }) => {
                      const updatedExpanded = _.takeRight(
                        [...(expandedExpirations || []), option.value],
                        OptionChain.MaxExpandedExpirations
                      );
                      setExpandedExpirations(updatedExpanded);
                    }}
                    placeholder={'Split Screen'}
                    isSearchable={false}
                  />
                </>
              </div>
            </div>
          )}
          {expandedExpirations && expandedExpirations[1] && (
            <>
              <br />
              <div className="block-section block-section-fixed" />
              <br />
              <SplitOptionChainView
                index={1}
                expandedExpirations={expandedExpirations}
                setExpandedExpirations={setExpandedExpirations}
                symbol={symbol}
                dateForDiff={dateForDiff}
                rowValues2={rowValues2}
                setRowValues1={setRowValues1}
                setRowValues2={setRowValues2}
              />
              {rowValues2.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <div style={{ textAlign: 'center', width: '100%', fontSize: '0.75rem' }}>
                    <Loading size="small" />
                    <br />
                  </div>
                </div>
              ) : (
                <FrozenHeaderTable
                  columnWidths={columnWidths}
                  RowComponent={OptionChainTableRow}
                  className="chain"
                  tableKey="optionsChain"
                  responsiveDisabled={true}
                  columns={columnsDisplayed}
                  data={rowValues2}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

type Props = {
  index: number;
  expandedExpirations: ExtendedExpiration[];
  setExpandedExpirations: (newExpiration: ExtendedExpiration[]) => void;
  symbol: AssetSymbol;
  dateForDiff: dayjs.Dayjs;
  rowValues2: TableOptionPairData[];
  setRowValues1: (newRowValues: TableOptionPairData[]) => void;
  setRowValues2: (newRowValues: TableOptionPairData[]) => void;
};

const SplitOptionChainView: React.FC<Props> = ({
  index,
  expandedExpirations,
  setExpandedExpirations,
  symbol,
  dateForDiff,
  rowValues2,
  setRowValues1,
  setRowValues2,
}) => {
  const latestReceivedDate = useStateSelector(s => s.quotes.quoteMetadata.latestReceivedTime);
  const marketOpen = useStateSelector(s => isMarketOpenSelector(s));
  const marketCloseTime = useStateSelector(s => MarketCloseTimeSelector(s));
  const indexMarketCloseTime = useStateSelector(s => IndexMarketCloseTimeSelector(s));
  const timeBasedOnExpiration = useMemo(() => {
    const cstIndexMarketCloseTime = indexMarketCloseTime
      ? dayjs(indexMarketCloseTime as Dayjs).tz('America/Chicago')
      : dayjs();
    const cstMarketCloseTime = marketCloseTime ? dayjs(marketCloseTime as Dayjs).tz('America/Chicago') : dayjs();
    if (SYMBOLS_THAT_CAN_TRADED_AFTER_MARKET_CLOSE.includes(symbol.underlyingSymbol || '')) {
      // Cases when the symbol can be traded after the market close
      if (isToday(expandedExpirations[index].date)) {
        // Cases when the date is today
        if (expandedExpirations[index].optionExpirationType === OptionExpirationType.AM) {
          // Make the time be expired since AM Expirations can only be traded before the expiriation date
          if (indexMarketCloseTime) {
            return dayjs(expandedExpirations[index].date)
              .subtract(1, 'day')
              .hour(cstIndexMarketCloseTime.hour())
              .minute(cstIndexMarketCloseTime.minute() + 1)
              .second(0)
              .millisecond(0)
              .toDate();
          }
          return dayjs().toDate();
        } else {
          // Cases when the date is today and the expiration is PM, add market close time to the expiration date
          if (marketCloseTime) {
            return dayjs(expandedExpirations[index].date)
              .hour(cstMarketCloseTime.hour())
              .minute(cstMarketCloseTime.minute() + 1)
              .second(0)
              .millisecond(0)
              .toDate();
          }
          return dayjs().toDate();
        }
      } else {
        // Cases when the date is not today, add index market close time to the expiration date
        if (indexMarketCloseTime) {
          return dayjs(expandedExpirations[index].date)
            .hour(cstIndexMarketCloseTime.hour())
            .minute(cstIndexMarketCloseTime.minute() + 1)
            .second(0)
            .millisecond(0)
            .toDate();
        }
        return dayjs().toDate();
      }
    } else {
      // Cases when the symbol cannot be traded after the market close, add market close time to the expiration date
      if (marketCloseTime) {
        return dayjs(expandedExpirations[index].date)
          .hour(cstMarketCloseTime.hour())
          .minute(cstMarketCloseTime.minute() + 1)
          .second(0)
          .millisecond(0)
          .toDate();
      }
      return dayjs().toDate();
    }
  }, [marketOpen, indexMarketCloseTime, marketCloseTime, symbol, expandedExpirations, index]);

  const { timeLeft, parsed } = useParsedCountdown(timeBasedOnExpiration, 1000);

  const countdownTitle = useCallback(
    (parsed: ParsedMs) => {
      const marketClosed =
        parsed.hours <= 0 && parsed.minutes <= 0 ? 'Expired' : `Expiring in ${DisplayTimeTilClose(parsed)}`;

      const marketOpens = `Expiring in ${DisplayTimeTilClose(parsed)}`;
      let quoteDataAsOf = 'Data refresh time not available';
      if (!_.isNil(latestReceivedDate)) {
        const now = dayjs().valueOf();
        if (now <= latestReceivedDate) {
          quoteDataAsOf = 'Data as of a few seconds ago';
        } else {
          quoteDataAsOf = `Data as of ${dayjs(latestReceivedDate).fromNow()}`;
        }
      }
      if (marketOpen) {
        return `${marketClosed}. ${quoteDataAsOf}.`;
      } else {
        return `${marketOpens}. ${quoteDataAsOf}.`;
      }
    },
    [latestReceivedDate, marketOpen]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', alignItems: 'center' }}>
      <div
        style={{
          textAlign: 'center',
          fontSize: '0.75rem',
        }}
      >
        <span style={{ color: 'white' }} className="col-auto">
          Calls <i className="fal fa-arrow-right"></i>
        </span>
      </div>
      <div
        style={{
          textAlign: 'center',
          width: '80%',
          fontSize: '0.75rem',
        }}
      >
        <span className="paging pl-4">
          <button
            disabled={expandedExpirations[index].index === 0}
            className="btn btn-blend"
            onClick={() => {
              const temp = { ...expandedExpirations[index] };
              temp.index = 0;
              expandedExpirations[index] = temp;
              setExpandedExpirations(expandedExpirations);
            }}
            title="First Strike"
          >
            <i className="far fa-angle-double-up" />
          </button>{' '}
          <button
            disabled={expandedExpirations[index].index === 0}
            className="btn btn-blend"
            onClick={() => {
              const temp = { ...expandedExpirations[index] };
              temp.index = temp.index ? (temp.index - 10 >= 0 ? temp.index - 10 : 0) : 0;
              expandedExpirations[index] = temp;
              setExpandedExpirations(expandedExpirations);
            }}
            title="Previous 10"
          >
            <i className="far fa-angle-up" />
          </button>{' '}
          <button
            disabled={expandedExpirations[index].index === 0}
            className="btn btn-blend"
            onClick={() => {
              const temp = { ...expandedExpirations[index] };
              temp.index = temp.index ? (temp.index - 1 >= 0 ? temp.index - 1 : 0) : 0;
              expandedExpirations[index] = temp;
              setExpandedExpirations(expandedExpirations);
            }}
            title="Previous 1"
          >
            -
          </button>
        </span>
        <span className="paging">
          <button
            disabled={
              expandedExpirations[index].index != null &&
              expandedExpirations[index].closestStrikeIndex != null &&
              Math.abs(expandedExpirations[index].index - expandedExpirations[index].closestStrikeIndex) < 5
            }
            className="btn btn-blend"
            onClick={() => {
              const temp = { ...expandedExpirations[index] };
              temp.index = undefined;
              expandedExpirations[index] = temp;
              setExpandedExpirations(expandedExpirations);
            }}
            title={`Go to Current Price | ${countdownTitle(parsed)}`}
          >
            {expandedExpirations[index].date ? toShort(expandedExpirations[index].date) : ''}{' '}
            {expirationDisplayInfo(expandedExpirations[index], symbol).label} (
            {dayjs(expandedExpirations[index].date)
              .utc()
              .diff(dayjs(dateForDiff.format('YYYY-MM-DD')), 'day') - 1}{' '}
            {dayjs(expandedExpirations[index].date)
              .utc()
              .diff(dayjs(dateForDiff.format('YYYY-MM-DD')), 'day') === 2
              ? 'day'
              : 'days'}
            ){' '}
          </button>
        </span>
        <span className="paging">
          <button
            disabled={
              expandedExpirations[index].index ===
              (expandedExpirations[index].length ? expandedExpirations[index].length : 0) - 10
            }
            className="btn btn-blend"
            onClick={() => {
              const temp = { ...expandedExpirations[index] };
              if (temp.index !== undefined && temp.length !== undefined) {
                if (temp.index + 1 < temp.length - 10) temp.index = temp.index + 1;
                else temp.index = temp.length - 10;
              }
              expandedExpirations[index] = temp;
              setExpandedExpirations(expandedExpirations);
            }}
            title="Next 1"
          >
            +
          </button>{' '}
          <button
            disabled={
              expandedExpirations[index].index ===
              (expandedExpirations[index].length ? expandedExpirations[index].length : 0) - 10
            }
            className="btn btn-blend"
            onClick={() => {
              const temp = { ...expandedExpirations[index] };
              if (temp.index !== undefined && temp.length !== undefined) {
                if (temp.index + 10 < temp.length - 10) temp.index = temp.index + 10;
                else temp.index = temp.length - 10;
              }
              expandedExpirations[index] = temp;
              setExpandedExpirations(expandedExpirations);
            }}
            title="Next 10"
          >
            <i className="far fa-angle-down" />
          </button>{' '}
          <button
            disabled={
              expandedExpirations[index].index ===
              (expandedExpirations[index].length ? expandedExpirations[index].length : 0) - 10
            }
            className="btn btn-blend"
            onClick={() => {
              const temp = { ...expandedExpirations[index] };
              temp.index = temp.length ? temp.length - 10 : 0;
              expandedExpirations[index] = temp;
              setExpandedExpirations(expandedExpirations);
            }}
            title="Last Strike"
          >
            <i className="far fa-angle-double-down" />
          </button>
        </span>
        <span className="paging">
          <button
            className="btn btn-blend"
            title="Remove"
            onClick={() => {
              if (index === 0) {
                if (expandedExpirations.length === 1) {
                  setRowValues1([]);
                  setRowValues2([]);
                  setExpandedExpirations([]);
                } else {
                  setRowValues1(rowValues2);
                  setRowValues2([]);
                  setExpandedExpirations(expandedExpirations.slice(1));
                }
              } else {
                setRowValues2([]);
                const newExp = expandedExpirations.shift();
                if (newExp) setExpandedExpirations([newExp]);
              }
            }}
            type="button"
          >
            <i className="fas fa-times-circle"></i>
          </button>
        </span>
      </div>
      <div
        style={{
          textAlign: 'center',
          fontSize: '0.75rem',
        }}
      >
        <span style={{ color: 'white' }} className="col-auto align-self-end">
          <i className="fal fa-arrow-left"></i> Puts
        </span>
      </div>
    </div>
  );
};

export default SplitOptionChainView;
